import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';

// jl-design-system
import Form from 'jl-design-system/components/form/Form';
import { Message } from 'jl-design-system/components/message/Message';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';
import formatDayPickerDays from 'jl-design-system/utils/dayPicker/dayPicker';

// Types
import { AppDispatch, RootState } from 'types/RootState.types';
import { CollectorDetailsProps } from './CollectorDetails.types';

// Config
import { submitClickAndCollectDeliveryDetails } from '@redux/actions/click-and-collect/clickAndCollectActions';
import getCollectorDetailsFormConfig, { COLLECTOR_DETAILS_FORM_ID } from '@utils/form/configs/collectorDetails';
import { reportErrorToNewRelic } from '@utils/logging/logging-utils';
import collectorDetailsState from './CollectorDetails.state';

// components
import ClickAndCollectCutOffPassedModal from '@components/click-and-collect-cut-off-passed-modal';
import CollectionDatesUnavailable from '@components/collection-dates-unavailable';
import Container from '@components/container';
import Heading from '@components/heading';

// styles
import styles from './collector-details.scss';

const CollectorDetails = ({
  disabled,
  showSecureIcon,
}: CollectorDetailsProps) => {
  const dispatch: AppDispatch = useDispatch();

  const {
    collectionPointDates,
    collectorDetailsFormValues,
    focusOnSelectedDayMessage,
    getCollectionPointDatesApiCallActive,
    getCollectionPointDetailsCallActive,
    isCollectPlus,
    isGiftReceiptSelected,
    phoneNumber,
    selectedDay,
    showCollectionDatesCalendar,
    ukMobileNumberIsRequired,
    proceedingToPayment,
    cutOffPassedDuringOrderSubmit,
    showDateError,
    collectionPointDatesEmpty,
    showLimitedAvailablityMessage,
    storeType,
  } = useSelector((state: RootState) => collectorDetailsState(
    state,
    COLLECTOR_DETAILS_FORM_ID,
    'giftReceipt',
  ));

  const onFormSubmit = (formValues: any) => {
    try {
      return dispatch(submitClickAndCollectDeliveryDetails({ formValues }));
    } catch (error: any) {
      dispatch(reportErrorToNewRelic({
        error,
        errorDescription: 'redux-form-submission-error',
      }));
    }

    return null;
  };

  const dates = useMemo(() => collectionPointDates?.
    filter((date: { isAvailable?: boolean }) => date.isAvailable)?.
    map((filteredDate: { date?: string }) => filteredDate.date), [collectionPointDates]);

  let limitedDaysMessage;

  if (showLimitedAvailablityMessage) {
    const formattedDays = formatDayPickerDays(dates, 7, true);
    const availableDays = (formattedDays?.[0] ?? []).filter((day: { active?: boolean }) => day.active);

    if (availableDays.length <= 3) {
      limitedDaysMessage = (
        <Container marginBottom="2">
          <Message
            body="Once your order is ready to collect it will typically be available for 7 days."
            title="Due to high demand there are limited days available to choose from"
            type="warning"
          />
        </Container>
      );
    }
  }

  const collectionDatesCalendarFormConfig =
    showCollectionDatesCalendar && collectionPointDates?.length
      ? {
        containerClassName: styles.dayPicker,
        dates,
        daysToShowPerPage: 7,
        disabled,
        focusOnSelectedDayMessage,
        instruction: 'Please provide a number we can contact you on if there are any queries with your order.',
        maxPagesToShow: 1,
        selectedDay,
      }
      : undefined;

  const formConfig = getCollectorDetailsFormConfig(
    {
      isGiftReceiptSelected,
      ...collectionDatesCalendarFormConfig,
      isCollectPlus,
    },
  );

  return (
    <div className={styles.container}>
      <ClickAndCollectCutOffPassedModal handleSubmit={() => onFormSubmit(collectorDetailsFormValues)} />
      <Container renderIf={!getCollectionPointDetailsCallActive}>
        <div data-testid="collection-point-details">
          <Container marginBottom="2" renderIf={ukMobileNumberIsRequired}>
            <Message
              body="We're unable to use your saved contact number as it's not a UK mobile number."
              dataTestId="collector-details-uk-mobile-required-message"
              title="Please provide a UK mobile number"
              type="information"
            />
          </Container>

          <Container marginBottom="2" renderIf={cutOffPassedDuringOrderSubmit}>
            <Message
              body={
                isCollectPlus
                  ? 'Please try and place your order again.'
                  : 'Please choose another day to complete your order.'
              }
              dataTestId="collector-details-cut-off-passed-during-submit"
              title="Sorry, we have been unable to place your order as your collection slot is no longer available"
              type="error"
            />
          </Container>

          {!showCollectionDatesCalendar && (
            <p data-testid="collector-details-provide-contact-number">
              {storeType} shops require a UK mobile number so you can be texted when the order is ready to collect. If
              you do not have a UK mobile number, please try a different shop.
            </p>
          )}

          <Container renderIf={showCollectionDatesCalendar}>
            <Heading
              label="Choose a collection day"
              marginBottom="0.5"
              tag="h4"
              testId="collector-details-choose-day"
              type="s"
            />

            <p>You can collect your order within seven days of your selected date.</p>

            {limitedDaysMessage}
          </Container>

          <Container
            className={styles.spinnerContainer}
            renderIf={showCollectionDatesCalendar && getCollectionPointDatesApiCallActive}
          >
            <LoadingSpinner />
          </Container>

          {showDateError && <CollectionDatesUnavailable collectionPointDatesEmpty={collectionPointDatesEmpty} />}

          {!showDateError && (
            <Form
              config={formConfig}
              data-testid="collector-details-form"
              destroyOnUnmount
              disabled={disabled}
              form={formConfig.id}
              initialValues={{ phoneNumber }}
              multiFormValidation
              onSubmit={onFormSubmit}
              submitButtonClassName={cx(styles.button, {
                [styles.lockIcon]: showSecureIcon,
              })}
              submittingOverride={proceedingToPayment || getCollectionPointDatesApiCallActive}
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default CollectorDetails;
